import * as React from 'react';
import { observer } from 'mobx-react-lite';
import { Layout, Button, Form, Input, Select, message, Tabs, Tag } from 'antd';
import LayoutHeader from '../../../../components/LayoutHeader';
import { Content } from 'antd/lib/layout/layout';
import { Utils } from '../../../common/services/Utils';
import PromptTemplateStore from '../../stores/PromptTemplatesStore';
import { EditPromptTemplateStore, PropmtDependentPackagesStore } from '../../stores';
import {
    validateJson,
    prepareBlocksForSubmission,
    initializeBlocksFromTemplate,
    validateOrchestratedBlocks
} from '../../misc';
import './PromptTemplate.less';
import JsonEditor from './JsonEditor';
import { AppPermissions } from '../../../authorization/Permissions';
import { HasPermission } from '../../../authorization/components/HasPermission';
import ReparseDependentPackagesButton from './ReparseDependentPackagesButton';
import { PromptTemplateBlock, PromptTemplateStructureEnum } from '../../types';
import { BlocksManager } from '.';
import OrchestrationValidationAlert from './OrchestrationValidationAlert';
import { RouterStore } from '../../../common/stores';
import { Prompt } from 'react-router';
import PromptTemplateVersionsTab from './PromptTemplateVersionsTab';

const { TabPane } = Tabs;

type Props = {
    store: PromptTemplateStore | undefined;
    editStore: EditPromptTemplateStore;
    propmtDependentPackagesStore: PropmtDependentPackagesStore;
    router?: RouterStore;
};

const EditPromptTemplate: React.FC<Props> = ({ store, editStore, propmtDependentPackagesStore }) => {
    const [form] = Form.useForm();

    const [editorValue, setEditorValue] = React.useState<string>('');
    const [jsonIsInvalid, setJsonIsInvalid] = React.useState(false);
    const [blocks, setBlocks] = React.useState<PromptTemplateBlock[]>([]);
    const [collapsedKeys, setCollapsedKeys] = React.useState<string[]>([]);
    const [hasUnsavedChanges, setHasUnsavedChanges] = React.useState(false);
    const [templateStructure, setTemplateStructure] = React.useState<PromptTemplateStructureEnum>(
        PromptTemplateStructureEnum.Merged
    );
    const [orchestrationErrors, setOrchestrationErrors] = React.useState<string[]>([]);

    const markAsChanged = () => setHasUnsavedChanges(true);

    React.useEffect(() => {
        if (store && store.currentProject && store.selectedTemplateId) {
            editStore.initializeWithTemplate(store.selectedTemplateId);
        }

        return () => editStore.dispose();
    }, [store, editStore]);

    React.useEffect(() => {
        if (editStore.selectedTemplate) {
            const currentStructure =
                editStore.selectedTemplate.promptTemplateStructure || PromptTemplateStructureEnum.Merged;

            form.setFieldsValue({
                title: editStore.selectedTemplate.title,
                description: editStore.selectedTemplate.description,
                body: editStore.selectedTemplate.body,
                promptTemplateStructure: currentStructure
            });

            setTemplateStructure(currentStructure);

            const result = initializeBlocksFromTemplate(editStore.selectedTemplate);
            setBlocks(result.blocks);
            setCollapsedKeys(result.collapsedKeys);
            setEditorValue(editStore.selectedTemplate.structuredOutput ?? '');
        }
    }, [form, editStore.selectedTemplate]);

    if (!store || !store.selectedTemplateId) {
        return null;
    }

    const handleValueChange = (value: string) => {
        setJsonIsInvalid(false);
        setEditorValue(value);
        markAsChanged();
    };

    const handleGoBack = () => {
        store.navigateToList();
    };

    const handleSubmit = async (shouldCreateVersion: boolean = false): Promise<boolean> => {
        try {
            // Clear previous orchestration errors
            setOrchestrationErrors([]);

            const jsonIsValid = validateJson(editorValue);

            if (!jsonIsValid) {
                setJsonIsInvalid(true);
                return false;
            }

            const values = await form.validateFields();
            const currentStructure = values.promptTemplateStructure || PromptTemplateStructureEnum.Merged;

            // Validate orchestration rules for Orchestrated templates
            const orchestrationValidation = validateOrchestratedBlocks(blocks, currentStructure);

            if (!orchestrationValidation.isValid) {
                setOrchestrationErrors(orchestrationValidation.errors);
                message.error('Failed to save the template. Please, see errors for more information.');
                return false;
            }

            const blocksForSubmission = prepareBlocksForSubmission(blocks);
            setHasUnsavedChanges(false);

            const result = await store.editTemplate(
                values.title,
                values.description,
                editStore.selectedTemplate!.body,
                editorValue,
                currentStructure,
                blocksForSubmission,
                shouldCreateVersion
            );

            return result;
        } catch (error) {
            setHasUnsavedChanges(true);
            console.log('Form validation failed');
            return false;
        }
    };

    const getVersionTag = () => {
        const template = editStore.selectedTemplate;
        if (!template) {
            return <></>;
        }
        if (template.isDraft) {
            return (
                <Tag color="orange" style={{ marginRight: 30 }}>
                    Active Draft (based on v{template.currentVersion})
                </Tag>
            );
        }
        return (
            <Tag key="active-version" color="green" style={{ marginRight: 30 }}>
                Active v{template.currentVersion}
            </Tag>
        );
    };

    return (
        <Form form={form} layout="vertical">
            <Layout className="prompt-template-layout">
                <Prompt when={hasUnsavedChanges} message="You have unsaved changes. Are you sure you want to leave?" />
                <LayoutHeader
                    subtitle={Utils.getSubtitle(store.currentProject)}
                    helpMessage=""
                    title={editStore.selectedTemplate?.title ?? 'Edit Prompt Template'}
                    buttons={[
                        ...(hasUnsavedChanges
                            ? [
                                  <span key="unsaved-changes-message" className="unsaved-changes-message">
                                      Unsaved changes
                                  </span>
                              ]
                            : []),
                        getVersionTag(),
                        <HasPermission
                            key="reparse-dependent-packages"
                            entityId={store?.currentProject?.id}
                            permissionClaim={AppPermissions.CanEditProcessDocuments}
                        >
                            <ReparseDependentPackagesButton
                                store={propmtDependentPackagesStore}
                                updatePromptTemplate={() => handleSubmit()}
                            />
                        </HasPermission>,
                        <Button
                            key="prompt-templates-go-back"
                            data-id="button-go-to-list"
                            className="light"
                            size="large"
                            onClick={handleGoBack}
                        >
                            Go back
                        </Button>,
                        <Button
                            key="prompt-templates-submit"
                            data-id="button-submit"
                            type="primary"
                            size="large"
                            onClick={() => handleSubmit()}
                        >
                            Save as draft
                        </Button>,
                        <Button
                            key="prompt-templates-publish"
                            data-id="button-publish"
                            size="large"
                            className="light"
                            onClick={() => handleSubmit(true)}
                        >
                            Publish
                        </Button>
                    ]}
                />
                <Layout>
                    <Content className="prompt-template-content">
                        <div className="content-padding">
                            <Tabs defaultActiveKey="template">
                                <TabPane tab="Template" key="template">
                                    <Form.Item name="blocks" hidden>
                                        <Input type="hidden" />
                                    </Form.Item>

                                    <Form.Item
                                        name="title"
                                        label="Title"
                                        rules={[
                                            { required: true, message: 'Please enter a template title' },
                                            { whitespace: true, message: 'Template title cannot be empty' }
                                        ]}
                                    >
                                        <Input
                                            placeholder="Enter template name"
                                            defaultValue={store.selectedTemplate?.title || ''}
                                            onChange={markAsChanged}
                                        />
                                    </Form.Item>
                                    <Form.Item name="description" label="Description">
                                        <Input placeholder="Enter template description" onChange={markAsChanged} />
                                    </Form.Item>

                                    <Form.Item
                                        name="promptTemplateStructure"
                                        label="Template Structure"
                                        tooltip="Merged = single merged prompt; no step-by-step execution implied.
                                Orchestrated = explicit flow with fan-out (concurrent Processing) and fan-in (Post-Processing)."
                                        rules={[{ required: true, message: 'Please select a template structure' }]}
                                        initialValue={PromptTemplateStructureEnum.Merged}
                                    >
                                        <Select
                                            placeholder="Select template structure"
                                            onChange={(value: PromptTemplateStructureEnum) => {
                                                setTemplateStructure(value);
                                                setOrchestrationErrors([]); // Clear errors when structure changes
                                                markAsChanged();
                                            }}
                                        >
                                            <Select.Option value={PromptTemplateStructureEnum.Merged}>
                                                Merged
                                            </Select.Option>
                                            <Select.Option value={PromptTemplateStructureEnum.Orchestrated}>
                                                Orchestrated
                                            </Select.Option>
                                        </Select>
                                    </Form.Item>

                                    {/* Orchestration Validation Errors */}
                                    <OrchestrationValidationAlert errors={orchestrationErrors} />

                                    <BlocksManager
                                        blocks={blocks}
                                        collapsedKeys={collapsedKeys}
                                        onBlocksChange={newBlocks => {
                                            setBlocks(newBlocks);
                                            setOrchestrationErrors([]); // Clear errors when blocks change
                                            markAsChanged();
                                        }}
                                        onCollapsedKeysChange={setCollapsedKeys}
                                        templateStructure={templateStructure}
                                    />

                                    <JsonEditor
                                        value={editorValue}
                                        onChange={handleValueChange}
                                        jsonIsInvalid={jsonIsInvalid}
                                    />
                                </TabPane>
                                <TabPane tab="Versions" key="versions">
                                    <PromptTemplateVersionsTab
                                        templateId={store.selectedTemplateId!}
                                        store={store}
                                        editStore={editStore}
                                    />
                                </TabPane>
                            </Tabs>
                        </div>
                    </Content>
                </Layout>
            </Layout>
        </Form>
    );
};

export default observer(EditPromptTemplate);
